import { Matrix3, Matrix4, SphericalHarmonics3, Vector3 } from "three";
import { CAR_ONLY_LIGHTING } from "./carOnlyLighting";

export function validLightEstimate(estimate) {
  const sh = estimate?.sphericalHarmonicsCoefficients;
  const rgb = estimate?.primaryLightIntensity;
  const direction = estimate?.primaryLightDirection;
  return sh?.length === 27 && Array.from(sh).every(Number.isFinite) &&
    [rgb?.x, rgb?.y, rgb?.z].every(n => Number.isFinite(n) && n >= 0) &&
    [direction?.x, direction?.y, direction?.z].every(Number.isFinite) &&
    Math.hypot(direction.x, direction.y, direction.z) > 0.001;
}

export function validLightPose(pose) {
  const matrix = pose?.transform?.matrix;
  if (matrix?.length !== 16 || !Array.from(matrix).every(Number.isFinite)) return false;
  const determinant = new Matrix4().fromArray(matrix).determinant();
  return Number.isFinite(determinant) && Math.abs(determinant) > 0.00000001;
}

// Store only bounded, world-oriented samples. A uniform SH gain preserves signs
// and color; clamping individual coefficients would change the lighting shape.
export function boundedWorldLightSample(estimate, rotation) {
  if (!validLightEstimate(estimate) || !rotation?.toArray().every(Number.isFinite)) return null;
  const rgb = estimate.primaryLightIntensity;
  const rawDirect = Math.max(rgb.x, rgb.y, rgb.z);
  const directIntensity = Math.min(rawDirect * CAR_ONLY_LIGHTING.directGain, CAR_ONLY_LIGHTING.maxDirectIntensity);
  const directRGB = [rgb.x, rgb.y, rgb.z].map(value => rawDirect ? value / rawDirect * directIntensity : 0);

  // Normalizing before rotation also handles very large but finite device data
  // without overflowing the quadratic SH transform.
  const source = Array.from(estimate.sphericalHarmonicsCoefficients);
  const divisor = Math.max(1, ...source.map(Math.abs));
  const harmonics = rotateLightHarmonics(source.map(value => value / divisor), rotation, new SphericalHarmonics3()).toArray();
  const normalizedMax = Math.max(...harmonics.map(Math.abs));
  const weightedMagnitude = normalizedMax ? Math.min(divisor * CAR_ONLY_LIGHTING.diffuseGain,
    CAR_ONLY_LIGHTING.maxDiffuseCoefficient / normalizedMax) : CAR_ONLY_LIGHTING.diffuseGain;
  const diffuseGain = normalizedMax ? Math.min(CAR_ONLY_LIGHTING.diffuseGain,
    CAR_ONLY_LIGHTING.maxDiffuseCoefficient / divisor / normalizedMax) : CAR_ONLY_LIGHTING.diffuseGain;
  const sh = harmonics.map(value => value * weightedMagnitude);

  const rawDirection = estimate.primaryLightDirection;
  const directionDivisor = Math.max(Math.abs(rawDirection.x), Math.abs(rawDirection.y), Math.abs(rawDirection.z));
  const direction = new Vector3(rawDirection.x / directionDivisor, rawDirection.y / directionDivisor,
    rawDirection.z / directionDivisor).normalize().applyQuaternion(rotation).normalize().toArray();
  return { directRGB, sh, direction, directGain: rawDirect ? directIntensity / rawDirect : CAR_ONLY_LIGHTING.directGain, diffuseGain };
}

const median = values => {
  const sorted = [...values].sort((a, b) => a - b);
  const center = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[center] : (sorted[center - 1] + sorted[center]) / 2;
};

export function createLightCaptureWindow() {
  return { samples: [], validSeconds: 0 };
}

export function resetLightCaptureWindow(window) {
  window.samples = [];
  window.validSeconds = 0;
}

export function stabilizeRoomLight(window, sample, delta) {
  if (!sample) {
    resetLightCaptureWindow(window);
    return null;
  }
  window.samples.push(sample);
  if (window.samples.length > CAR_ONLY_LIGHTING.stabilizationSamples) window.samples.shift();
  // A paused frame cannot satisfy the stabilization dwell by itself.
  window.validSeconds += Number.isFinite(delta) && delta > 0 ? Math.min(delta, 0.1) : 0;
  if (window.samples.length < CAR_ONLY_LIGHTING.stabilizationSamples ||
    window.validSeconds + Number.EPSILON < CAR_ONLY_LIGHTING.stabilizationSeconds) return null;
  const components = key => sample[key].map((_, index) => median(window.samples.map(entry => entry[key][index])));
  const direction = new Vector3(...components("direction"));
  if (direction.lengthSq() < 0.000001) direction.fromArray(sample.direction);
  direction.normalize();
  return { directRGB: components("directRGB"), sh: components("sh"), direction: direction.toArray(),
    directGain: median(window.samples.map(entry => entry.directGain)),
    diffuseGain: median(window.samples.map(entry => entry.diffuseGain)),
    sampleCount: window.samples.length, validSeconds: window.validSeconds };
}

// Rotate the l=1 vector and l=2 symmetric traceless tensor. Parenting a Three
// LightProbe does NOT rotate its SH coefficients. Translation must not affect light.
export function rotateLightHarmonics(source, rotation, target) {
  const r = new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(rotation));
  const inverse = r.clone().transpose();
  const vector = new Vector3();
  const tensor = new Matrix3();
  for (let color = 0; color < 3; color++) {
    const c = i => source[i * 3 + color];
    const put = (i, value) => target.coefficients[i].setComponent(color, value);
    put(0, c(0));
    vector.set(c(3), c(1), c(2)).applyMatrix3(r);
    put(3, vector.x); put(1, vector.y); put(2, vector.z);
    tensor.set(
      -0.315392*c(6)+0.546274*c(8), 0.546274*c(4), 0.546274*c(7),
      0.546274*c(4), -0.315392*c(6)-0.546274*c(8), 0.546274*c(5),
      0.546274*c(7), 0.546274*c(5), 0.630784*c(6)
    ).premultiply(r).multiply(inverse);
    const e = tensor.elements;
    put(4, e[3]/0.546274); put(5, e[7]/0.546274); put(7, e[6]/0.546274);
    put(6, e[8]/0.630784); put(8, (e[0]-e[4])/1.092548);
  }
  return target;
}

export function supportsContactShadow(blendMode) {
  // An additive display cannot subtract light from the physical room.
  return blendMode !== "additive";
}
